// frontend/src/pages/LeaderboardPage.js
import React, { useEffect, useRef, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import api from '../api'
import Navbar from '../components/Navbar'

const font = "'Noto Serif JP', Georgia, serif"
const ink = '#1A0A00'
const muted = '#8A7A5A'
const accent = '#C41E3A'
const cardBorder = '#D4C8A8'
const bg = '#F5F0E8'

// ─── All-time standings columns (unchanged) ───────────────────────────────────
const AT_COLS = [
  { key: 'rank',       label: '#',      width: 28,  align: 'left',  sortable: false },
  { key: 'username',   label: 'Player', flex: 1,    align: 'left',  sortable: true  },
  { key: 'wins',       label: 'W',      width: 44,  align: 'right', sortable: true  },
  { key: 'losses',     label: 'L',      width: 44,  align: 'right', sortable: true  },
  { key: 'winPct',     label: '%',      width: 44,  align: 'right', sortable: true  },
  { key: 'yusho',      label: 'Yusho',  width: 56,  align: 'right', sortable: true  },
  { key: 'bashoCount', label: 'Basho',  width: 52,  align: 'right', sortable: true  },
]

const TOP_N = 10

// ─── Player stats columns ─────────────────────────────────────────────────────
// sticky cols have noRotate=true so their headers stay horizontal
// left = sticky pixel offset; bestFinish/avgFinish default sort ascending
// total cell widths: 28+104+50+58+56+54+48+60+56+52+52+52+90+90+90 = 940
const PS_COLS = [
  { key: 'rank',              label: '#',            width: 28,  align: 'center', sortable: false, sticky: true, left: 0,  noRotate: true },
  { key: 'username',          label: 'Name',          width: 104, align: 'left',   sortable: true,  sticky: true, left: 28, noRotate: true },
  { key: 'yusho',             label: 'Yusho',         width: 50,  align: 'right',  sortable: true,  desc: 'Tournament championships — finished 1st in a basho' },
  { key: 'podiums',           label: 'Podiums',       width: 58,  align: 'right',  sortable: true,  desc: 'Number of basho finished in the top 3' },
  { key: 'bestFinish',        label: 'Best Finish',   width: 56,  align: 'right',  sortable: true,  defaultDir: 'asc', desc: 'Best single-basho finishing position' },
  { key: 'avgFinish',         label: 'Avg Finish',    width: 54,  align: 'right',  sortable: true,  defaultDir: 'asc', desc: 'Average finishing position across all basho — lower is better' },
  { key: 'bashoCount',        label: 'Basho',         width: 48,  align: 'right',  sortable: true,  desc: 'Number of basho participated in' },
  { key: 'bestBasho',         label: 'Best Basho',    width: 60,  align: 'right',  sortable: true,  desc: 'Best single-basho win % in batting average format (.750 = 75%)' },
  { key: 'bestDay',           label: 'Best Day',      width: 56,  align: 'right',  sortable: true,  desc: 'Most correct picks in a single day' },
  { key: 'longestStreak',     label: 'Win Streak',    width: 52,  align: 'right',  sortable: true,  desc: 'Longest streak of consecutive correct picks' },
  { key: 'longestLossStreak', label: 'Loss Streak',   width: 52,  align: 'right',  sortable: true,  desc: 'Longest streak of consecutive incorrect picks' },
  { key: 'contrarian',        label: 'Contrarian',    width: 52,  align: 'right',  sortable: true,  desc: 'How often you pick against the majority — higher means more contrarian' },
  { key: 'favorite',          label: 'Favorite',      width: 90,  align: 'left',   sortable: true,  desc: 'Wrestler you have picked most frequently' },
  { key: 'lucky',             label: 'Lucky',         width: 90,  align: 'left',   sortable: true,  desc: 'Wrestler who has won the most times when you picked them' },
  { key: 'bogey',             label: 'Bogey',         width: 90,  align: 'left',   sortable: true,  desc: 'Wrestler who has lost the most times when you picked them' },
]

const STRING_COLS = new Set(['username', 'favorite', 'lucky', 'bogey'])

// ─── Helpers ──────────────────────────────────────────────────────────────────
function flashRef(ref) {
  if (!ref.current) return
  ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
  ref.current.classList.add('flash-highlight')
  setTimeout(() => ref.current?.classList.remove('flash-highlight'), 1200)
}

// Returns an array of 1-based ranks with ties (e.g. [1,2,3,3,5,6])
function computeRanks(arr, isTied) {
  const ranks = new Array(arr.length)
  let rank = 1
  for (let i = 0; i < arr.length; i++) {
    if (i > 0 && isTied(arr[i], arr[i - 1])) {
      ranks[i] = ranks[i - 1]
    } else {
      ranks[i] = rank
    }
    rank = i + 2
  }
  return ranks
}

function ordinal(n) {
  if (n == null) return '—'
  const s = ['th', 'st', 'nd', 'rd']
  const v = n % 100
  return n + (s[(v - 20) % 10] || s[v] || s[0])
}

// Format bestBasho as batting-average style: .775
function fmtBestBasho(v) {
  if (v === null || v === undefined) return '—'
  if (v >= 1) return '1.000'
  return '.' + Math.round(v * 1000).toString().padStart(3, '0')
}

function fmtPsCell(key, value) {
  if (value === null || value === undefined) return '—'
  switch (key) {
    case 'bestFinish':    return ordinal(value)
    case 'avgFinish':     return typeof value === 'number' ? value.toFixed(1) : value
    case 'bestBasho':     return fmtBestBasho(value)
    case 'contrarian':    return `${value}%`
    default:              return String(value)
  }
}

// ─────────────────────────────────────────────────────────────────────────────

export default function LeaderboardPage() {
  const { basho, day } = useParams()
  const currentUser = localStorage.getItem('username')

  // ── Group filter (non-default users only) ────────────────────────────────
  const userGroupId = localStorage.getItem('groupId')
  const isDefaultGroup = localStorage.getItem('isDefaultGroup') !== 'false'
  const [userGroupName, setUserGroupName] = useState(localStorage.getItem('groupName') || 'My Group')
  // Default to group view for non-default group members
  const [groupFilter, setGroupFilter] = useState(!isDefaultGroup && userGroupId ? userGroupId : null)

  // Keep group name fresh — admin may rename the group at any time
  useEffect(() => {
    api.get('/user/me')
      .then(res => {
        if (res.data.groupName) {
          setUserGroupName(res.data.groupName)
          localStorage.setItem('groupName', res.data.groupName)
        }
      })
      .catch(() => {})
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ── All-time standings state ───────────────────────────────────────────────
  const [allTime, setAllTime] = useState([])
  const [loading, setLoading] = useState(true)
  const [sort, setSort] = useState({ key: 'winPct', dir: 'desc' })
  const [showAllAt, setShowAllAt] = useState(false)

  const selfRowAtRef = useRef(null)
  const pendingJumpAt = useRef(false)

  useEffect(() => {
    if (pendingJumpAt.current) {
      pendingJumpAt.current = false
      setTimeout(() => flashRef(selfRowAtRef), 50)
    }
  }, [showAllAt])

  useEffect(() => {
    setLoading(true)
    const q = groupFilter ? `?groupId=${groupFilter}` : ''
    api.get(`/leaderboard/all-time${q}`)
      .then(res => setAllTime(res.data.standings || []))
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [groupFilter]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Player stats state ────────────────────────────────────────────────────
  const [psData, setPsData] = useState([])
  const [psLoading, setPsLoading] = useState(true)
  const [psSort, setPsSort] = useState({ key: 'yusho', dir: 'desc' })
  const [psTooltip, setPsTooltip] = useState(null)

  useEffect(() => {
    setPsLoading(true)
    const q = groupFilter ? `?groupId=${groupFilter}` : ''
    api.get(`/leaderboard/player-stats${q}`)
      .then(res => setPsData(res.data.players || []))
      .catch(console.error)
      .finally(() => setPsLoading(false))
  }, [groupFilter]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── All-time sort/display ─────────────────────────────────────────────────
  const handleSort = (key) => {
    setSort(s => s.key === key
      ? { key, dir: s.dir === 'desc' ? 'asc' : 'desc' }
      : { key, dir: 'desc' })
  }

  const sorted = [...allTime].sort((a, b) => {
    const mul = sort.dir === 'desc' ? -1 : 1
    if (sort.key === 'username') return mul * a.username.localeCompare(b.username)
    return mul * ((a[sort.key] ?? 0) - (b[sort.key] ?? 0))
  })
  const atRanks = computeRanks(sorted, (a, b) => {
    if (sort.key === 'username') return false
    if (a[sort.key] !== b[sort.key]) return false
    if (sort.key === 'winPct') return a.wins === b.wins
    return true
  })

  const selfIdxAt = currentUser ? sorted.findIndex(r => r.username === currentUser) : -1
  const showGapAt = !showAllAt && selfIdxAt >= TOP_N
  const visibleAt = showAllAt ? sorted : sorted.slice(0, Math.min(TOP_N, sorted.length))
  const selfRowAtData = showGapAt ? sorted[selfIdxAt] : null

  const jumpToSelfAt = () => {
    if (!showAllAt) { pendingJumpAt.current = true; setShowAllAt(true) }
    else flashRef(selfRowAtRef)
  }

  // ── Player stats sort/display ─────────────────────────────────────────────
  const handlePsSort = (key) => {
    setPsSort(s => {
      if (s.key === key) return { key, dir: s.dir === 'desc' ? 'asc' : 'desc' }
      const col = PS_COLS.find(c => c.key === key)
      return { key, dir: col?.defaultDir || 'desc' }
    })
  }

  const sortedPs = [...psData].sort((a, b) => {
    const mul = psSort.dir === 'desc' ? -1 : 1
    const key = psSort.key
    const aVal = a[key], bVal = b[key]

    // Nulls always last
    if (aVal === null && bVal === null) { /* fall through to secondary */ }
    else if (aVal === null) return 1
    else if (bVal === null) return -1
    else {
      const cmp = STRING_COLS.has(key)
        ? aVal.localeCompare(bVal)
        : aVal - bVal
      if (cmp !== 0) return mul * cmp
    }

    // Secondary: winPct desc
    if (key !== 'winPct') {
      const aw = a.winPct ?? -1, bw = b.winPct ?? -1
      if (aw !== bw) return bw - aw
    }
    // Tertiary: name asc
    return a.username.localeCompare(b.username)
  })

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <>
      <style>{`
        @keyframes rowFlash {
          0% { background-color: rgba(196, 30, 58, 0.18) !important; }
          100% { background-color: transparent; }
        }
        .flash-highlight { animation: rowFlash 1.2s ease-out; }
        @media (max-width: 768px) {
          .lb-table-card { overflow-x: auto !important; }
          .lb-at-row { min-width: 380px; }
          .ps-cell { font-size: 11px !important; }
          .ps-legend { display: block !important; }
        }
      `}</style>
      <div style={s.page}>
        <Navbar currentPage="leaderboard" />
        <div style={s.container}>

          {/* ── Group selector (non-default group members only) ─────────────── */}
          {!isDefaultGroup && userGroupId && (
            <div style={s.groupBar}>
              <span style={s.groupBarLabel}>Viewing:</span>
              {[
                { value: null, label: 'All Players' },
                { value: userGroupId, label: userGroupName },
              ].map(opt => (
                <button
                  key={opt.value || 'all'}
                  onClick={() => { setGroupFilter(opt.value); setShowAllAt(false) }}
                  style={s.groupBarBtn(groupFilter === opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}

          {/* ── Section 1: All-time standings (unchanged) ───────────────────── */}
          <section style={s.section}>
            <div style={s.redLine} />
            <h2 style={s.sectionTitle}>All-Time Standings</h2>
            {loading ? (
              <p style={s.emptyMsg}>Loading…</p>
            ) : sorted.length === 0 ? (
              <p style={s.emptyMsg}>No data yet.</p>
            ) : (
              <div className="lb-table-card" style={s.tableCard}>
                <div className="lb-at-row" style={s.atHeaderRow}>
                  {AT_COLS.map(col => (
                    <div
                      key={col.key}
                      style={{
                        ...s.atHeaderCell,
                        width: col.width,
                        flex: col.flex,
                        textAlign: col.align,
                        cursor: col.sortable ? 'pointer' : 'default',
                        color: sort.key === col.key ? accent : muted,
                      }}
                      onClick={() => col.sortable && handleSort(col.key)}
                    >
                      {col.label}
                      {sort.key === col.key && (
                        <span style={{ marginLeft: 2, color: accent }}>
                          {sort.dir === 'desc' ? '▾' : '▴'}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
                <div style={s.atHeaderDivider} />
                {visibleAt.map((r, i) => {
                  const self = r.username === currentUser
                  return (
                    <div
                      key={r.username}
                      ref={self ? selfRowAtRef : null}
                      className="lb-at-row"
                      style={{ ...s.atDataRow, ...(self ? s.atSelfRow : {}) }}
                    >
                      <div style={{ ...s.atCell, width: 28, color: muted, fontSize: 11 }}>{atRanks[i]}</div>
                      <div style={{ ...s.atCell, flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Link
                          to={`/profile/${r.username}`}
                          title={r.username}
                          style={{ color: self ? accent : ink, fontWeight: self ? 700 : 400, textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}
                          onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                          onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
                        >{r.username}</Link>
                        {r.isBot && <span style={s.botBadge}>🤖</span>}
                      </div>
                      <div style={{ ...s.atCell, width: 44, textAlign: 'right' }}>{r.wins}</div>
                      <div style={{ ...s.atCell, width: 44, textAlign: 'right' }}>{r.losses}</div>
                      <div style={{ ...s.atCell, width: 44, textAlign: 'right' }}>{r.winPct !== null ? `${r.winPct}%` : '—'}</div>
                      <div style={{ ...s.atCell, width: 56, textAlign: 'right', fontWeight: r.yusho > 0 ? 700 : 400 }}>
                        {r.yusho || '—'}
                      </div>
                      <div style={{ ...s.atCell, width: 52, textAlign: 'right', color: muted }}>
                        {r.bashoCount || '—'}
                      </div>
                    </div>
                  )
                })}
                {showGapAt && selfRowAtData && (
                  <>
                    <div style={s.gapRow}>···</div>
                    <div ref={selfRowAtRef} className="lb-at-row" style={{ ...s.atDataRow, ...s.atSelfRow }}>
                      <div style={{ ...s.atCell, width: 28, color: muted, fontSize: 11 }}>{atRanks[selfIdxAt]}</div>
                      <div style={{ ...s.atCell, flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Link
                          to={`/profile/${selfRowAtData.username}`}
                          title={selfRowAtData.username}
                          style={{ color: accent, fontWeight: 700, textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}
                          onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                          onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
                        >{selfRowAtData.username}</Link>
                        {selfRowAtData.isBot && <span style={s.botBadge}>🤖</span>}
                      </div>
                      <div style={{ ...s.atCell, width: 44, textAlign: 'right' }}>{selfRowAtData.wins}</div>
                      <div style={{ ...s.atCell, width: 44, textAlign: 'right' }}>{selfRowAtData.losses}</div>
                      <div style={{ ...s.atCell, width: 44, textAlign: 'right' }}>{selfRowAtData.winPct !== null ? `${selfRowAtData.winPct}%` : '—'}</div>
                      <div style={{ ...s.atCell, width: 56, textAlign: 'right', fontWeight: selfRowAtData.yusho > 0 ? 700 : 400 }}>
                        {selfRowAtData.yusho || '—'}
                      </div>
                      <div style={{ ...s.atCell, width: 52, textAlign: 'right', color: muted }}>
                        {selfRowAtData.bashoCount}
                      </div>
                    </div>
                  </>
                )}
                {sorted.length > TOP_N && (
                  <div style={s.showAllRow}>
                    <button onClick={() => setShowAllAt(a => !a)} style={s.showAllBtn}>
                      {showAllAt ? 'Show less ↑' : `Show all ${sorted.length} players ↓`}
                    </button>
                    {!showAllAt && selfIdxAt >= TOP_N && currentUser && (
                      <button onClick={jumpToSelfAt} style={s.jumpBtn}>↓ Jump to my rank</button>
                    )}
                  </div>
                )}
              </div>
            )}
          </section>

          {/* ── Section 2: Player Stats ──────────────────────────────────────── */}
          <section style={s.section}>
            <div style={s.redLine} />
            <h2 style={s.sectionTitle}>Player Stats</h2>
            <p style={s.psSubtitle}>Detailed comparison across all players · Click any column to sort · Hover headers for descriptions</p>
            {psLoading ? (
              <p style={s.emptyMsg}>Loading…</p>
            ) : sortedPs.length === 0 ? (
              <p style={s.emptyMsg}>No data yet.</p>
            ) : (
              <>
              <div style={s.tableCard}>
                <div style={s.psTableWrap}>
                  {/* Header */}
                  <div style={s.psHeaderRow}>
                    {PS_COLS.map(col => {
                      const isActive = psSort.key === col.key
                      return (
                        <div
                          key={col.key}
                          className="ps-header-cell"
                          style={{
                            ...s.psHeaderCell,
                            width: col.width,
                            cursor: col.sortable ? 'pointer' : 'default',
                            ...(col.sticky ? {
                              position: 'sticky', left: col.left, zIndex: 3,
                              background: '#FDFCF9',
                              ...(col.key === 'username' ? { boxShadow: '2px 0 3px rgba(0,0,0,0.08)' } : {}),
                            } : {}),
                          }}
                          onClick={() => col.sortable && handlePsSort(col.key)}
                          onMouseEnter={col.desc ? (e) => {
                            const rect = e.currentTarget.getBoundingClientRect()
                            setPsTooltip({ x: rect.left + rect.width / 2, y: rect.top, text: col.desc })
                          } : undefined}
                          onMouseLeave={col.desc ? () => setPsTooltip(null) : undefined}
                        >
                          {col.noRotate ? (
                            /* # and Name — horizontal, no rotation */
                            <span style={{
                              fontSize: 12,
                              color: isActive ? accent : muted,
                              fontFamily: font,
                              letterSpacing: 1,
                              textTransform: 'uppercase',
                              userSelect: 'none',
                              display: 'flex', alignItems: 'center', gap: 2,
                            }}>
                              {col.label}
                              {isActive && <span style={{ color: accent }}>{psSort.dir === 'desc' ? '▾' : '▴'}</span>}
                            </span>
                          ) : (
                            /* All other columns — rotated 90° left */
                            <>
                              <span style={{
                                writingMode: 'vertical-rl',
                                transform: 'rotate(180deg)',
                                fontSize: 12,
                                color: isActive ? accent : muted,
                                fontFamily: font,
                                letterSpacing: 1,
                                textTransform: 'uppercase',
                                userSelect: 'none',
                                whiteSpace: 'nowrap',
                                display: 'block',
                              }}>
                                {col.label}
                              </span>
                              {isActive && (
                                <span style={{ color: accent, fontSize: 10, lineHeight: 1, marginTop: 2 }}>
                                  {psSort.dir === 'desc' ? '▾' : '▴'}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      )
                    })}
                  </div>
                  <div style={s.atHeaderDivider} />

                  {/* Data rows */}
                  {sortedPs.map((row, i) => {
                    const self = row.username === currentUser
                    const rowBg = self ? '#FEF0F0' : '#FFFFFF'
                    return (
                      <div
                        key={row.username}
                        style={{
                          ...s.psDataRow,
                          background: rowBg,
                          ...(self ? { borderLeft: `3px solid ${accent}`, paddingLeft: 5 } : {}),
                        }}
                      >
                        {PS_COLS.map(col => {
                          const stickyStyle = col.sticky ? {
                            position: 'sticky', left: col.left, zIndex: 2,
                            background: rowBg,
                            ...(col.key === 'username' ? { boxShadow: '2px 0 3px rgba(0,0,0,0.08)' } : {}),
                          } : {}

                          if (col.key === 'rank') return (
                            <div key="rank" className="ps-cell" style={{ ...s.psCell, width: 28, textAlign: 'center', color: muted, ...stickyStyle }}>
                              {i + 1}
                            </div>
                          )
                          if (col.key === 'username') return (
                            <div key="username" className="ps-cell" style={{ ...s.psCell, width: 104, display: 'flex', alignItems: 'center', gap: 3, overflow: 'hidden', ...stickyStyle }} title={row.username}>
                              <Link
                                to={`/profile/${row.username}`}
                                style={{ color: self ? accent : ink, fontWeight: self ? 700 : 400, textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: '1 1 auto' }}
                                onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                                onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
                              >{row.username}</Link>
                              {row.isBot && <span style={s.botBadgeSm}>🤖</span>}
                            </div>
                          )
                          return (
                            <div
                              key={col.key}
                              className="ps-cell"
                              style={{ ...s.psCell, width: col.width, textAlign: col.align || 'right', ...stickyStyle }}
                              title={STRING_COLS.has(col.key) ? (row[col.key] || undefined) : undefined}
                            >
                              {fmtPsCell(col.key, row[col.key])}
                            </div>
                          )
                        })}
                      </div>
                    )
                  })}
                </div>
              </div>
              {/* Mobile column legend — hidden on desktop, shown on mobile */}
              <div className="ps-legend" style={s.psLegend}>
                <div style={s.psLegendTitle}>Column Guide</div>
                {PS_COLS.filter(c => c.desc).map(col => (
                  <div key={col.key} style={s.psLegendRow}>
                    <div style={s.psLegendKey}>{col.label}</div>
                    <div style={s.psLegendDesc}>{col.desc}</div>
                  </div>
                ))}
              </div>
              </>
            )}
          </section>

        </div>
      </div>
      {psTooltip && (
        <div style={{
          position: 'fixed',
          left: psTooltip.x,
          top: psTooltip.y,
          transform: 'translate(-50%, calc(-100% - 8px))',
          background: '#1A0A00',
          color: '#F5F0E8',
          fontSize: 11,
          fontFamily: 'Georgia, serif',
          padding: '6px 10px',
          borderRadius: 3,
          whiteSpace: 'normal',
          width: 180,
          lineHeight: 1.5,
          zIndex: 9999,
          pointerEvents: 'none',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
        }}>
          {psTooltip.text}
        </div>
      )}
    </>
  )
}

const s = {
  page: {
    minHeight: '100vh',
    background: bg,
    backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(180,160,120,0.06) 3px, rgba(180,160,120,0.06) 4px)',
    color: ink,
  },
  container: { maxWidth: 900, margin: '0 auto', padding: '28px 20px 60px' },
  section: { marginBottom: 48 },

  // Group filter bar (non-default users only)
  groupBar: {
    display: 'flex', alignItems: 'center', gap: 8,
    marginBottom: 24, flexWrap: 'wrap',
  },
  groupBarLabel: { fontSize: 11, color: muted, fontFamily: font, letterSpacing: 1, textTransform: 'uppercase' },
  groupBarBtn: (active) => ({
    padding: '5px 14px', borderRadius: 20, fontSize: 11, fontFamily: font, letterSpacing: 0.5,
    textTransform: 'uppercase',
    cursor: 'pointer', border: `1px solid ${active ? ink : cardBorder}`,
    background: active ? ink : 'transparent',
    color: active ? '#F5F0E8' : muted,
    transition: 'all 0.15s',
  }),
  redLine: { width: 28, height: 3, background: accent, marginBottom: 8 },
  sectionTitle: {
    fontSize: 16, fontWeight: 700, color: ink, margin: '0 0 6px',
    fontFamily: font, letterSpacing: 0.5,
  },
  psSubtitle: {
    fontSize: 11, color: muted, fontFamily: 'Georgia, serif',
    margin: '0 0 16px', letterSpacing: 0.2,
  },
  emptyMsg: { color: muted, fontFamily: font, fontStyle: 'italic', fontSize: 13, margin: 0 },

  tableCard: {
    background: '#FFFFFF', border: `1px solid ${cardBorder}`,
    borderRadius: 4, overflow: 'hidden',
  },

  // ── All-time table (unchanged) ────────────────────────────────────────────
  atHeaderRow: {
    display: 'flex', alignItems: 'center',
    padding: '8px 14px', gap: 4, background: '#FDFCF9',
  },
  atHeaderCell: {
    fontSize: 9, fontFamily: font, letterSpacing: 2,
    textTransform: 'uppercase', userSelect: 'none',
    display: 'flex', alignItems: 'center', gap: 2,
  },
  atHeaderDivider: { borderBottom: `2px solid ${cardBorder}` },
  atDataRow: {
    display: 'flex', alignItems: 'center',
    padding: '9px 14px', gap: 4,
    borderBottom: `1px solid ${bg}`,
    background: '#FFFFFF',
  },
  atSelfRow: {
    background: '#FEF0F0',
    borderLeft: `3px solid ${accent}`,
    paddingLeft: 11,
  },
  atCell: { fontSize: 13, fontFamily: font, color: ink },

  gapRow: {
    padding: '5px 14px', color: '#C4B890', fontSize: 13,
    fontFamily: font, textAlign: 'center', letterSpacing: 4,
    borderBottom: `1px solid ${bg}`,
  },
  showAllRow: {
    padding: '10px 14px',
    display: 'flex', gap: 16, alignItems: 'center',
    borderTop: `1px solid ${bg}`,
  },
  showAllBtn: {
    background: 'none', border: 'none', padding: 0,
    color: muted, fontSize: 11, fontFamily: font,
    cursor: 'pointer', letterSpacing: 0.3,
  },
  jumpBtn: {
    background: 'none', border: 'none', padding: 0,
    color: accent, fontSize: 11, fontFamily: font,
    cursor: 'pointer', letterSpacing: 0.3,
  },

  // ── Player stats table ────────────────────────────────────────────────────
  psTableWrap: {
    overflowX: 'auto',
  },
  psHeaderRow: {
    display: 'flex', alignItems: 'flex-end', flexWrap: 'nowrap',
    background: '#FDFCF9', minWidth: 940,
    padding: '0 8px',
  },
  // height: 130px accommodates 12px font × ~11 char labels (e.g. "Loss Streak")
  psHeaderCell: {
    flexShrink: 0,
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end',
    height: 130,
    paddingBottom: 6,
    paddingTop: 4,
  },
  psDataRow: {
    display: 'flex', alignItems: 'center', flexWrap: 'nowrap',
    minWidth: 940, padding: '0 8px',
    borderBottom: `1px solid ${bg}`,
  },
  psCell: {
    fontSize: 12, fontFamily: font, color: ink,
    flexShrink: 0, padding: '7px 4px',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },

  // ── Bot badges ────────────────────────────────────────────────────────────
  botBadge: {
    flexShrink: 0,
    fontSize: 9, letterSpacing: 0.8, fontFamily: font,
    color: '#8A7A5A', background: '#EDE8DC',
    padding: '1px 5px', borderRadius: 2,
    display: 'inline-block', whiteSpace: 'nowrap', verticalAlign: 'middle',
  },
  botBadgeSm: {
    flexShrink: 0,
    fontSize: 10, lineHeight: 1,
    display: 'inline-block', verticalAlign: 'middle',
  },

  // ── Mobile legend (hidden on desktop via className) ───────────────────────
  psLegend: {
    display: 'none',
    marginTop: 10,
    background: '#FFFFFF',
    border: `1px solid ${cardBorder}`,
    borderRadius: 4,
    padding: '14px 18px 6px',
  },
  psLegendTitle: {
    fontSize: 9, color: muted, fontFamily: font,
    letterSpacing: 2, textTransform: 'uppercase',
    marginBottom: 10,
  },
  psLegendRow: {
    display: 'grid', gridTemplateColumns: '100px 1fr', gap: 8,
    padding: '5px 0', borderBottom: `1px solid ${bg}`,
  },
  psLegendKey: {
    fontSize: 11, fontFamily: font, color: ink, fontWeight: 600,
  },
  psLegendDesc: {
    fontSize: 11, fontFamily: 'Georgia, serif', color: muted, lineHeight: 1.4,
  },
}
